import { useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { routes } from '@/config/routes';
import { useAuth } from '@/features/auth/context';
import { hasAnyRole } from '@/features/auth/utils';
import { useAppStore } from '@/state/appStore';

import { categoryNavigation } from './navigation';

type MobileNavigationProps = {
  menuButtonRef: React.RefObject<HTMLButtonElement | null>;
  onSearch: () => void;
};

export function MobileNavigation({ menuButtonRef, onSearch }: MobileNavigationProps) {
  const open = useAppStore((state) => state.mobileNavigationOpen);
  const setOpen = useAppStore((state) => state.setMobileNavigationOpen);
  const drawerRef = useRef<HTMLElement>(null);
  const location = useLocation();
  const previousPathRef = useRef(location.pathname);
  const { status, user } = useAuth();

  useEffect(() => {
    if (previousPathRef.current !== location.pathname) {
      setOpen(false);
      previousPathRef.current = location.pathname;
    }
  }, [location.pathname, setOpen]);

  useEffect(() => {
    if (!open) return;

    const previousOverflow = document.body.style.overflow;
    const menuButton = menuButtonRef.current;
    document.body.style.overflow = 'hidden';
    drawerRef.current?.focus();

    function handleKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }

    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = previousOverflow;
      menuButton?.focus();
    };
  }, [menuButtonRef, open, setOpen]);

  if (!open) return null;

  const close = () => setOpen(false);
  const openSearch = () => {
    close();
    onSearch();
  };

  return (
    <div className="mobile-navigation" role="presentation">
      <button className="mobile-navigation__backdrop" aria-label="Close menu" onClick={close} />
      <aside
        aria-label="Mobile navigation"
        className="mobile-navigation__drawer"
        ref={drawerRef}
        tabIndex={-1}
      >
        <div className="mobile-navigation__header">
          <strong>Menu</strong>
          <button className="icon-button" type="button" aria-label="Close menu" onClick={close}>
            <i className="pi pi-times" aria-hidden="true" />
          </button>
        </div>
        <button className="mobile-search-action" type="button" onClick={openSearch}>
          <i className="pi pi-search" aria-hidden="true" /> Search Emazy
        </button>
        <nav aria-label="Mobile primary navigation">
          <NavLink to={routes.products} onClick={close}>
            Shop all products
          </NavLink>
          <p className="mobile-navigation__label">Shop by category</p>
          {categoryNavigation.map((item) => (
            <NavLink key={item.to} to={item.to} onClick={close}>
              <i className={item.icon} aria-hidden="true" /> {item.label}
            </NavLink>
          ))}
          <p className="mobile-navigation__label">Learn more</p>
          <NavLink to={routes.howItWorks} onClick={close}>
            How It Works
          </NavLink>
          <NavLink to={routes.about} onClick={close}>
            About Emazy
          </NavLink>
          <NavLink to={routes.faq} onClick={close}>
            FAQ
          </NavLink>
          <NavLink to={routes.contact} onClick={close}>
            Contact
          </NavLink>
        </nav>
        <div className="mobile-navigation__utilities">
          {status === 'initializing' ? (
            <span className="mobile-navigation__account-loading">Loading account…</span>
          ) : user ? (
            <>
              <strong className="mobile-navigation__account-name">Hi, {user.firstName}</strong>
              <NavLink to={routes.dashboard} onClick={close}>
                <i className="pi pi-user" aria-hidden="true" /> Dashboard
              </NavLink>
              <NavLink to={routes.orders} onClick={close}>
                Orders
              </NavLink>
              {hasAnyRole(user, ['admin']) && (
                <NavLink to={routes.admin} onClick={close}>
                  Admin portal
                </NavLink>
              )}
              {hasAnyRole(user, ['admin', 'workshop']) && (
                <NavLink to={routes.workshop} onClick={close}>
                  Workshop portal
                </NavLink>
              )}
              <NavLink to={routes.logout} onClick={close}>
                Sign out
              </NavLink>
            </>
          ) : (
            <>
              <NavLink to={routes.login} onClick={close}>
                <i className="pi pi-user" aria-hidden="true" /> Sign in
              </NavLink>
              <NavLink to={routes.register} onClick={close}>
                Create account
              </NavLink>
            </>
          )}
          <NavLink to={routes.checkout} onClick={close}>
            <i className="pi pi-shopping-bag" aria-hidden="true" /> Cart (0)
          </NavLink>
        </div>
      </aside>
    </div>
  );
}
